import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEdit2, FiEye } from 'react-icons/fi';
import { fetchLeads } from '../api/leadService';
import { normalizeLead } from '../utils/leadModel';

const CLOSED_STATUSES = new Set(['Converted', 'Lost', 'Rejected']);

const TASK_TITLE_BY_STATUS = {
  New: 'Initial Outreach',
  Contacted: 'Follow-up Call',
  Qualified: 'Qualification Review',
  Nurturing: 'Nurture Sequence Follow-up',
  Converted: 'Post-Conversion Handover',
  Lost: 'Close-Lost Analysis',
  Rejected: 'Rejection Notes Review',
};

const SLA_DAYS_BY_STATUS = {
  New: 1,
  Contacted: 2,
  Qualified: 2,
  Nurturing: 3,
  Converted: 0,
  Lost: 0,
  Rejected: 0,
};

function toDate(value) {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate()
  );
}

function addDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function taskState(lead, dueAt, now) {
  if (CLOSED_STATUSES.has(lead.status)) return 'Completed';
  if (isSameDay(dueAt, now)) return 'Due Today';
  if (dueAt < now) return 'Overdue';
  return 'Open';
}

function buildTasks(leads) {
  const now = new Date();

  return leads
    .map((lead) => {
      const baseline = toDate(lead.updatedAt) || toDate(lead.createdAt) || now;
      const dueAt = addDays(baseline, SLA_DAYS_BY_STATUS[lead.status] ?? 2);
      const state = taskState(lead, dueAt, now);

      return {
        id: lead.id,
        leadId: lead.id,
        title: TASK_TITLE_BY_STATUS[lead.status] || 'Follow-up Task',
        contactName: lead.fullName,
        contactEmail: lead.email,
        companyName: lead.companyName,
        priority: lead.priority,
        status: lead.status,
        state,
        owner: lead.assignedTo || 'Unassigned',
        dueAt,
        dueLabel: new Intl.DateTimeFormat('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        }).format(dueAt),
        source: lead.source,
        notes: lead.raw?.notes || '',
      };
    })
    .sort((a, b) => a.dueAt.getTime() - b.dueAt.getTime());
}

function ownerLabel(owner) {
  if (!owner || owner === 'Unassigned') return 'Unassigned';
  return owner.length > 16 ? `${owner.slice(0, 8)}...${owner.slice(-4)}` : owner;
}

function TasksPage() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [activeTask, setActiveTask] = useState(null);

  useEffect(() => {
    let mounted = true;

    async function loadAllLeads() {
      setLoading(true);
      setError('');
      try {
        let page = 1;
        const combined = [];

        while (page <= 30) {
          const res = await fetchLeads({ page, limit: 100 });
          const rows = Array.isArray(res.data) ? res.data : [];
          combined.push(...rows.map(normalizeLead));
          if (!res.pagination?.has_next_page) break;
          page += 1;
        }

        if (mounted) setLeads(combined);
      } catch (err) {
        if (mounted) setError(err?.response?.data?.message || 'Unable to load tasks.');
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadAllLeads();
    return () => {
      mounted = false;
    };
  }, []);

  const tasks = useMemo(() => buildTasks(leads), [leads]);

  const filteredTasks = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tasks.filter((task) => {
      const bySearch = !q
        || task.title.toLowerCase().includes(q)
        || task.contactName.toLowerCase().includes(q)
        || task.contactEmail.toLowerCase().includes(q)
        || task.companyName.toLowerCase().includes(q);
      const byState = !stateFilter || task.state === stateFilter;
      const byPriority = !priorityFilter || task.priority === priorityFilter;
      return bySearch && byState && byPriority;
    });
  }, [tasks, search, stateFilter, priorityFilter]);

  const summary = useMemo(() => ({
    total: tasks.length,
    dueToday: tasks.filter((t) => t.state === 'Due Today').length,
    overdue: tasks.filter((t) => t.state === 'Overdue').length,
    completed: tasks.filter((t) => t.state === 'Completed').length,
  }), [tasks]);

  return (
    <div className="dashboard-page">
      <div className="page-title-row">
        <h2>Tasks</h2>
      </div>

      <section className="dashboard-kpis tasks-kpis">
        <article className="dashboard-kpi-card tasks-kpi-card tasks-kpi-card--total">
          <span>Total Tasks</span>
          <strong>{summary.total}</strong>
        </article>
        <article className="dashboard-kpi-card tasks-kpi-card tasks-kpi-card--due-today">
          <span>Due Today</span>
          <strong>{summary.dueToday}</strong>
        </article>
        <article className="dashboard-kpi-card tasks-kpi-card tasks-kpi-card--overdue">
          <span>Overdue</span>
          <strong>{summary.overdue}</strong>
        </article>
        <article className="dashboard-kpi-card tasks-kpi-card tasks-kpi-card--completed">
          <span>Completed</span>
          <strong>{summary.completed}</strong>
        </article>
      </section>

      <div className="filter-bar">
        <input
          className="filter-input"
          placeholder="Search task, contact, email, company..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="filter-select" value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}>
          <option value="">All States</option>
          <option value="Open">Open</option>
          <option value="Due Today">Due Today</option>
          <option value="Overdue">Overdue</option>
          <option value="Completed">Completed</option>
        </select>
        <select className="filter-select" value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
          <option value="">All Priorities</option>
          <option value="Low">Low</option>
          <option value="Medium">Medium</option>
          <option value="High">High</option>
          <option value="Urgent">Urgent</option>
        </select>
      </div>

      {loading ? <p>Loading tasks...</p> : null}
      {error ? <p className="error-text">{error}</p> : null}

      {!loading && !error ? (
        <div className="dashboard-panel dashboard-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Task</th>
                <th>Contact</th>
                <th>Company</th>
                <th>Priority</th>
                <th>State</th>
                <th>Due</th>
                <th>Owner</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTasks.length ? (
                filteredTasks.map((task) => (
                  <tr key={task.id}>
                    <td>{task.title}</td>
                    <td>
                      <button type="button" className="lead-name-btn" onClick={() => setActiveTask(task)}>
                        {task.contactName}
                      </button>
                    </td>
                    <td>{task.companyName}</td>
                    <td>{task.priority}</td>
                    <td>
                      <span className={`task-state-pill task-state-pill--${task.state.toLowerCase().replace(' ', '-')}`}>
                        {task.state}
                      </span>
                    </td>
                    <td>{task.dueLabel}</td>
                    <td className="mono-cell">{ownerLabel(task.owner)}</td>
                    <td>
                      <div className="table-actions">
                        <button
                          type="button"
                          className="icon-action-btn"
                          title="View task"
                          aria-label="View task"
                          data-tooltip="View task"
                          onClick={() => setActiveTask(task)}
                        >
                          <FiEye />
                        </button>
                        <button
                          type="button"
                          className="icon-action-btn"
                          title="Open lead"
                          aria-label="Open lead"
                          data-tooltip="Open lead"
                          onClick={() => navigate(`/leads/${task.leadId}/edit`)}
                        >
                          <FiEdit2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="8">No tasks found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      ) : null}

      {activeTask ? (
        <>
          <div className="drawer-backdrop" onClick={() => setActiveTask(null)} />
          <aside className="lead-drawer">
            <div className="drawer-header">
              <h3>{activeTask.title}</h3>
              <button className="modal-close" onClick={() => setActiveTask(null)} aria-label="Close">x</button>
            </div>

            <div className="drawer-section">
              <p className="drawer-section-title">Task Details</p>
              <div className="drawer-row">
                <span className="drawer-label">Contact</span>
                <span className="drawer-value">{activeTask.contactName}</span>
              </div>
              <div className="drawer-row">
                <span className="drawer-label">Email</span>
                <span className="drawer-value">{activeTask.contactEmail}</span>
              </div>
              <div className="drawer-row">
                <span className="drawer-label">Company</span>
                <span className="drawer-value">{activeTask.companyName}</span>
              </div>
              <div className="drawer-row">
                <span className="drawer-label">Source</span>
                <span className="drawer-value">{activeTask.source}</span>
              </div>
              <div className="drawer-row">
                <span className="drawer-label">State</span>
                <span className="drawer-value">
                  <span className={`task-state-pill task-state-pill--${activeTask.state.toLowerCase().replace(' ', '-')}`}>
                    {activeTask.state}
                  </span>
                </span>
              </div>
              <div className="drawer-row">
                <span className="drawer-label">Due Date</span>
                <span className="drawer-value">{activeTask.dueLabel}</span>
              </div>
              <div className="drawer-row">
                <span className="drawer-label">Owner</span>
                <span className="drawer-value mono-cell">{activeTask.owner}</span>
              </div>
            </div>

            <div className="drawer-section">
              <p className="drawer-section-title">Notes</p>
              <p className="drawer-notes">{activeTask.notes || 'No notes available.'}</p>
            </div>

            <div className="drawer-actions">
              <button type="button" className="primary-btn" onClick={() => navigate(`/leads/${activeTask.leadId}/edit`)}>
                Open Lead
              </button>
              <button type="button" className="ghost-btn" onClick={() => setActiveTask(null)}>
                Close
              </button>
            </div>
          </aside>
        </>
      ) : null}
    </div>
  );
}

export default TasksPage;
